import { Button, Group, Stack, Switch, Text } from "@mantine/core"
import { useEffect, useState } from "react"
import { ErrorAlert, PageHeader, SectionCard } from "atlas_forecasting_app/components/common/page_elements"
import { apiFetch } from "atlas_forecasting_app/lib/api_fetch"
import { RouterLink } from "atlas_forecasting_app/lib/router"

export function AccountSettingsPage({ currentUser, onCurrentUserUpdate, onLogout }) {
  const [questionsPublicByDefault, setQuestionsPublicByDefault] = useState(!!currentUser?.questions_public_by_default)
  const [forecastsPublicByDefault, setForecastsPublicByDefault] = useState(!!currentUser?.forecasts_public_by_default)
  const [saving, setSaving] = useState(false)
  const [loggingOut, setLoggingOut] = useState(false)
  const [error, setError] = useState("")
  const [saved, setSaved] = useState("")

  useEffect(() => {
    setQuestionsPublicByDefault(!!currentUser?.questions_public_by_default)
    setForecastsPublicByDefault(!!currentUser?.forecasts_public_by_default)
  }, [currentUser?.questions_public_by_default, currentUser?.forecasts_public_by_default])

  const save = async () => {
    setSaving(true)
    try {
      const data = await apiFetch("/api/me/settings", {
        method: "PATCH",
        body: {
          questions_public_by_default: questionsPublicByDefault,
          forecasts_public_by_default: forecastsPublicByDefault,
        },
      })
      if (data.user) onCurrentUserUpdate?.(data.user)
      setSaved("Settings saved.")
      setError("")
    } catch (e) {
      setError(e.message)
      setSaved("")
    } finally {
      setSaving(false)
    }
  }

  const handleLogout = async () => {
    if (!onLogout) return

    setLoggingOut(true)
    try {
      await onLogout()
    } finally {
      setLoggingOut(false)
    }
  }

  return (
    <Stack gap="lg">
      <PageHeader
        eyebrow="Account"
        variant="account"
        title="Account settings"
        subtitle="Choose the default visibility for new questions and for your forecasts on public questions."
        action={
          <Button component={RouterLink} to="/account/balance-history" variant="light">
            Balance history
          </Button>
        }
      />

      <ErrorAlert message={error} />

      <SectionCard variant="form" title="Defaults" subtitle="These choices affect how new questions and forecasts start.">
        <Stack gap="lg">
          <Switch
            checked={questionsPublicByDefault}
            onChange={(event) => setQuestionsPublicByDefault(event.currentTarget.checked)}
            label="Make the questions I create public by default"
            description="New questions you create start private unless you turn this on. Questions that are imported from external sources (such as Metaculus or Kalshi) are always public."
          />

          <Switch
            checked={forecastsPublicByDefault}
            onChange={(event) => setForecastsPublicByDefault(event.currentTarget.checked)}
            label="Make my forecasts on public questions public by default"
            description="Forecasts on private questions always stay private. Turning this on affects only new forecasts made on already-public questions."
          />

          <Group justify="space-between" align="center">
            <Text size="sm" c={saved ? "green" : "dimmed"}>
              {saved || "Changes apply to new questions and new forecasts."}
            </Text>
            <Button onClick={save} loading={saving}>
              Save settings
            </Button>
          </Group>
        </Stack>
      </SectionCard>

      <SectionCard variant="utility" title="Session" subtitle="Manage your current sign-in session.">
        <Group justify="space-between" align="center">
          <Text size="sm" c="dimmed">
            Need to leave Atlas on this device? Sign out here.
          </Text>
          <Button color="red" variant="light" onClick={handleLogout} loading={loggingOut}>
            Log out
          </Button>
        </Group>
      </SectionCard>
    </Stack>
  )
}
